// SectionRenderer.tsx - Renders the correct section editor based on activeSection
import React from 'react';
import CoverPageEditor from './components/editors/CoverPageEditor';
import DocumentReferenceEditor from './components/editors/DocumentReferenceEditor';
import ExecutiveSummaryEditor from './components/editors/ExecutiveSummaryEditor';
import DisclaimerEditor from './components/editors/DisclaimerEditor';
import MethodologyEditor from './components/editors/MethodologyEditor';
import RecommendationsEditor from './components/editors/RecommendationsEditor';
import ConclusionEditor from './components/editors/ConclusionEditor';
import FindingsEditor from './components/editors/FindingsEditor';
import TargetDetailsEditor from './components/editors/TargetDetailsEditor';
import ProjectObjectivesEditor from './components/editors/ProjectObjectivesEditor';
import ScopeEditor from './components/editors/ScopeEditor';
import FindingsSummaryEditor from './components/editors/FindingsSummaryEditor';
import VulnerabilityRatingsEditor from './components/editors/VulnerabilityRatingsEditor';
import CriticalFindingsEditor from './components/editors/CriticalFindingsEditor';
import HighFindingsEditor from './components/editors/HighFindingsEditor';
import MediumFindingsEditor from './components/editors/MediumFindingsEditor';
import LowFindingsEditor from './components/editors/LowFindingsEditor';
import InfoFindingsEditor from './components/editors/InfoFindingsEditor';
import KeyFindingsEditor from './components/editors/KeyFindingsEditor';
import BrandingEditor from './components/editors/BrandingEditor';
import MethodologySelectorEditor from './components/editors/MethodologySelectorEditor';
import FindingConfigEditor from './components/editors/FindingConfigEditor';
import ExecutiveSummaryEditorV2 from './components/editors/ExecutiveSummaryEditorV2';
import ScopeEditorV2 from './components/editors/ScopeEditorV2';
import OwaspTop10EditorV2 from './components/editors/OwaspTop10EditorV2';
import CertificateEditorV2 from './components/editors/CertificateEditorV2';
import AttestationEditor from './components/editors/AttestationEditor';
import RemediationRoadmapEditorV2 from './components/editors/RemediationRoadmapEditorV2';
import ToolsApproachEditorV2 from './components/editors/ToolsApproachEditorV2';
import MethodologyEditorV2 from './components/editors/MethodologyEditorV2';
import ConclusionEditorV2 from './components/editors/ConclusionEditorV2';
import { ReportData, DetailedFinding, Report } from './types/report.types';

interface SectionRendererProps {
  activeSection: string;
  currentData: any;
  sectionData: any;
  handleInputChange: (field: keyof ReportData, value: string) => void;
  handleHtmlChange: (field: keyof ReportData, value: string) => void;
  handleDataChange: (field: string, value: any) => void;
  handleFindingChange: (index: number, field: keyof DetailedFinding, value: string) => void;
  handleRemoveFinding: (index: number) => void;
  handleAddFinding: (severity: string) => void;
  handleTargetDetailsChange: (index: number, field: string, value: any) => void;
  handleAddTarget: () => void;
  handleRemoveTarget: (index: number) => void;
  handleTableChange: (fieldPath: string, value: number) => void;
  handleKeyFindingChange: (index: number, field: string, value: string) => void;
  handleAddKeyFinding: () => void;
  handleRemoveKeyFinding: (index: number) => void;
  handleRecommendationsChange: (recommendations: { title: string; description: string }[]) => void;
}

const SectionRenderer: React.FC<SectionRendererProps> = (props) => {
  const {
    activeSection,
    currentData,
    handleInputChange,
    handleHtmlChange,
    handleDataChange,
    handleFindingChange,
    handleRemoveFinding,
    handleAddFinding,
    handleTargetDetailsChange,
    handleAddTarget,
    handleRemoveTarget,
    handleTableChange,
    handleKeyFindingChange,
    handleAddKeyFinding,
    handleRemoveKeyFinding,
    handleRecommendationsChange
  } = props;

  switch (activeSection) {
    case 'branding':
      return <BrandingEditor reportData={currentData} onInputChange={handleInputChange} />;
    case 'cover':
      return <CoverPageEditor reportData={currentData} onInputChange={handleInputChange} />;
    case 'disclaimer':
      return <DisclaimerEditor reportData={currentData} onHtmlChange={handleHtmlChange} />;
    case 'document_reference':
      return <DocumentReferenceEditor reportData={currentData} onInputChange={handleInputChange} />;
    case 'executive_summary':
      if (currentData.template === 'template-2') {
        const mergedRecsData = { ...currentData, ...props.sectionData };
        return <ExecutiveSummaryEditorV2 reportData={mergedRecsData} onHtmlChange={handleHtmlChange} onRecommendationsChange={handleRecommendationsChange} onDataChange={handleDataChange} />;
      }
      return <ExecutiveSummaryEditor reportData={currentData} onHtmlChange={handleHtmlChange} onTableChange={handleTableChange} />;
    case 'scope':
      if (currentData.template === 'template-2') {
        return <ScopeEditorV2 reportData={currentData} onHtmlChange={handleHtmlChange} onTargetDetailsChange={handleTargetDetailsChange} onAddTarget={handleAddTarget} onRemoveTarget={handleRemoveTarget} onDataChange={handleDataChange} />;
      }
      return <ScopeEditor reportData={currentData} onHtmlChange={handleHtmlChange} onTargetDetailsChange={handleTargetDetailsChange} onAddTarget={handleAddTarget} onRemoveTarget={handleRemoveTarget} />;
    case 'project_objectives':
      return <ProjectObjectivesEditor reportData={currentData} onHtmlChange={handleHtmlChange} />;
    case 'findings_summary':
      return <FindingsSummaryEditor reportData={currentData} onTableChange={handleTableChange} />;
    case 'vulnerability_ratings':
      return <VulnerabilityRatingsEditor reportData={currentData} onDataChange={handleDataChange} />;
    case 'critical_findings':
      return <CriticalFindingsEditor reportData={currentData} onFindingChange={handleFindingChange} onRemoveFinding={handleRemoveFinding} onAddFinding={handleAddFinding} />;
    case 'high_findings':
      return <HighFindingsEditor reportData={currentData} onFindingChange={handleFindingChange} onRemoveFinding={handleRemoveFinding} onAddFinding={handleAddFinding} />;
    case 'medium_findings':
      return <MediumFindingsEditor reportData={currentData} onFindingChange={handleFindingChange} onRemoveFinding={handleRemoveFinding} onAddFinding={handleAddFinding} />;
    case 'low_findings':
      return <LowFindingsEditor reportData={currentData} onFindingChange={handleFindingChange} onRemoveFinding={handleRemoveFinding} onAddFinding={handleAddFinding} />;
    case 'info_findings':
      return <InfoFindingsEditor reportData={currentData} onFindingChange={handleFindingChange} onRemoveFinding={handleRemoveFinding} onAddFinding={handleAddFinding} />;
    case 'methodology':
      if (currentData.template === 'template-2') {
        return <MethodologyEditorV2 reportData={currentData} onDataChange={handleDataChange} />;
      }
      return <MethodologyEditor reportData={currentData} onHtmlChange={handleHtmlChange} />;
    case 'methodology_selector':
      return (
        <MethodologySelectorEditor
          value={currentData.methodology || {}}
          onChange={v => handleDataChange('methodology', v)}
        />
      );
    case 'findings':
      return <FindingsEditor reportData={currentData} onHtmlChange={handleHtmlChange} onDataChange={handleDataChange} onTableChange={handleTableChange} />;
    case 'recommendations':
      const mergedData = { ...currentData, ...props.sectionData };
      return <RecommendationsEditor reportData={mergedData} onRecommendationsChange={handleRecommendationsChange} />;
    case 'conclusion':
      if (currentData.template === 'template-2') {
        return <ConclusionEditorV2 reportData={currentData} onHtmlChange={handleHtmlChange} onDataChange={handleDataChange} />;
      }
      return <ConclusionEditor reportData={currentData} onHtmlChange={handleHtmlChange} />;
    case 'key_findings':
      return <KeyFindingsEditor reportData={currentData} onFindingChange={handleKeyFindingChange} onAddFinding={handleAddKeyFinding} onRemoveFinding={handleRemoveKeyFinding} />;
    case 'finding_config':
      return <FindingConfigEditor reportData={currentData} onDataChange={handleDataChange} onFindingChange={handleFindingChange} />;
    case 'owasp_top_10':
      return <OwaspTop10EditorV2 reportData={currentData} onDataChange={handleDataChange} />;
    case 'attestation_letter':
    case 'certificate':
      return <AttestationEditor reportData={currentData} onDataChange={handleDataChange} />;
    case 'remediation_roadmap':
      return <RemediationRoadmapEditorV2 reportData={currentData} onDataChange={handleDataChange} />;
    case 'tools_approach':
      return <ToolsApproachEditorV2 reportData={currentData} onDataChange={handleDataChange} />;
    default:
      return null;
  }
};

export default SectionRenderer; 